// ABOUTME: Loads the bounded filtered count required before a bulk selection.
// ABOUTME: Invalidates stale and out-of-order totals across modal load cycles.
import React from "react";
import { apiDirectCall } from "customHooks/useApi";
import useList from "customHooks/useList";
import { useAuthState } from "_ReactContext/AuthContext";
import {
    getBulkListCountParams,
    isBulkTotalReady,
    type IFilteredTotal
} from "./bulkListPolicy";
import type { BulkActionsType } from "./bulkActionCatalog";

export const useBulkListTotal = (
    loadTotal: boolean,
    bulkActionType?: BulkActionsType,
    otherGenericParams: Compleo.IObject = {}
) => {
    const [listData] = useList();
    const { company } = useAuthState();
    const params = getBulkListCountParams(
        listData.filtersInfo.filters,
        company.companyId,
        bulkActionType,
        otherGenericParams
    );
    const loadCycle = React.useRef(0);
    const wasLoading = React.useRef(false);
    if (loadTotal && !wasLoading.current) {
        loadCycle.current += 1;
    }
    wasLoading.current = loadTotal;
    const [retryCycle, setRetryCycle] = React.useState(0);

    const requestKey = JSON.stringify([
        listData.tempInfo.listApiAddress,
        params,
        loadCycle.current,
        retryCycle
    ]);
    const requestId = React.useRef(0);
    const [result, setResult] = React.useState<{
        requestKey: string;
        totalFiltered?: IFilteredTotal;
        ready: boolean;
        error: boolean;
    }>({ requestKey: "", ready: false, error: false });

    React.useEffect(() => {
        const currentRequestId = requestId.current + 1;
        requestId.current = currentRequestId;
        const abortController = new AbortController();

        if (!loadTotal) {
            setResult({ requestKey: "", ready: false, error: false });
            return;
        }

        setResult({ requestKey, ready: false, error: false });
        const load = async () => {
            try {
                const response = await apiDirectCall(
                    listData.tempInfo.listApiAddress,
                    "post",
                    params,
                    { signal: abortController.signal }
                );
                if (requestId.current !== currentRequestId) return;

                const totalFiltered = response?.data?.totalFiltered;
                const ready = isBulkTotalReady(
                    totalFiltered,
                    response?.data?.bulkProtocolVersion
                );
                setResult({
                    requestKey,
                    totalFiltered,
                    ready,
                    error: !ready
                });
            } catch {
                if (requestId.current === currentRequestId) {
                    setResult({ requestKey, ready: false, error: true });
                }
            }
        };
        load();

        return () => {
            abortController.abort();
            if (requestId.current === currentRequestId) {
                requestId.current += 1;
            }
        };
    }, [loadTotal, requestKey]);

    const hasCurrentResponse = result.requestKey === requestKey;

    return {
        totalFiltered: hasCurrentResponse ? result.totalFiltered : undefined,
        ready: loadTotal && hasCurrentResponse && result.ready,
        error: loadTotal && hasCurrentResponse && result.error,
        retry: () => setRetryCycle((cycle) => cycle + 1)
    };
};
